import { toast } from 'react-toastify';

/**
 * Backend ErrorType enum mapping to frontend error messages
 * Based on the backend GlobalExceptionHandler
 */
export const ERROR_TYPE_MESSAGES = {
  // User errors
  USER_NAME_NOT_FOUND: 'User not found',
  USER_NOT_FOUND: 'User not found',
  USER_NAME_ALREADY_EXISTS: 'Username already exists',
  
  // Video errors
  VIDEO_NOT_FOUND: 'Video not found',
  TRIMMED_VIDEO_NOT_FOUND: 'Trimmed video not found',
  EXPIRED_VIDEO: 'Video has expired',
  VIDEO_INVALID_PARAMETERS: 'Invalid video parameters',
  VIDEO_DIMENSION_ERROR: 'Video dimension error',
  VIDEO_ACCESS_EXCEPTION: 'Access denied for this video',
  VIDEO_ALREADY_PURCHASED: 'Video already purchased',
  INVALID_VIDEO_FILE: 'Invalid video file format',
  
  // Livestream errors
  LIVESTREAM_NOT_FOUND: 'Livestream not found',
  COURT_NOT_FOUND: 'Court not found',
  STREAM_KEY_NOT_FOUND: 'Stream key not found',
  VIEWER_NOT_FOUND: 'Viewer not found',
  LIVESTREAM_WATCH_URL_MISSING: 'Watch URL not available',
  LIVESTREAM_EXPIRED: 'Livestream has expired',
  LIVESTREAM_ALREADY_IN_PROGRESS: 'Livestream is already in progress',
  LIVESTREAM_ALREADY_COMPLETED: 'Livestream has already ended',
  LIVESTREAM_ALREADY_CANCELLED: 'Livestream has been cancelled',
  VIEWER_ALREADY_JOINED: 'You have already joined this livestream',
  LIVESTREAM_VIEW_LIMIT_REACHED: 'Viewer limit reached for this livestream',
  LIVESTREAM_PAYMENT_REQUIRED: 'Payment required to access this livestream',
  LIVESTREAM_FORBIDDEN: 'Access denied for this livestream',
  LIVESTREAM_NOT_IN_PROGRESS: 'Livestream is not in progress',
  LIVESTREAM_START_AFTER_END: 'Cannot start livestream after end time',
  LIVESTREAM_DESCRIPTION_EMPTY: 'Livestream description is required',
  LIVESTREAM_LOGO_EMPTY: 'Livestream logo is required',
  LIVESTREAM_SPONSORS_EMPTY: 'At least one sponsor is required',
  LIVESTREAM_PRICE_NEGATIVE: 'Livestream price cannot be negative',
  LIVESTREAM_UPDATE_NULL: 'No updates provided',
  TEAM_INFO_MISSING: 'Team information is missing',
  COURT_OR_OWNER_NULL: 'Court or owner information is missing',
  UNAUTHORIZED_ACCESS: 'You are not authorized to access this resource',
  
  // Purchase errors
  PURCHASE_NOT_FOUND: 'Purchase not found',
  PURCHASE_EXPIRED: 'Purchase has expired',
  PURCHASE_ALREADY_CANCELLED: 'Purchase already cancelled',
  PURCHASE_ALREADY_COMPLETED: 'Purchase already completed',
  PURCHASE_INVALID_STATUS: 'Invalid purchase status',
  
  // Discount errors
  DISCOUNT_CODE_NOT_FOUND: 'Discount code not found',
  DISCOUNT_CODE_EXPIRED: 'Discount code has expired',
  DUPLICATE_DISCOUNT_CODE: 'Discount code already exists',
  DUPLICATE_CODE: 'Code already exists',
  
  // Email errors
  EMAIL_SENDING_FAILED: 'Failed to send email',
  
  // General errors
  VALIDATION_ERROR: 'Validation error',
  OPTIMISTIC_LOCKING_FAILURE: 'Concurrent modification detected',
  GENERAL_ERROR: 'An unexpected error occurred',
  UNAUTHORIZED: 'Please log in to access this resource',
  FORBIDDEN: 'You do not have permission to access this resource',
  NOT_FOUND: 'Resource not found',
  CONFLICT: 'Resource conflict',
  PAYMENT_REQUIRED: 'Payment required',
  GONE: 'Resource no longer available',
  BAD_REQUEST: 'Invalid request',
  UNSUPPORTED_MEDIA_TYPE: 'Unsupported media type',
  INTERNAL_SERVER_ERROR: 'Internal server error'
};

/**
 * HTTP Status code to user-friendly message mapping
 */
export const HTTP_STATUS_MESSAGES = {
  400: 'Invalid request. Please check your input.',
  401: 'Please log in to access this resource.',
  403: 'You do not have permission to access this resource.',
  404: 'Resource not found.',
  409: 'Resource conflict. Please try again.',
  410: 'Resource no longer available.',
  415: 'Unsupported media type.',
  422: 'Validation error. Please check your input.',
  424: 'Payment required to access this resource.',
  500: 'Server error. Please try again later.',
  502: 'Service temporarily unavailable.',
  503: 'Service temporarily unavailable.',
  504: 'Request timeout. Please try again.'
};

/**
 * Enhanced error handler that aligns with backend ApiError structure
 * @param {Error} error - The error object
 * @param {Object} options - Additional options for error handling
 * @param {string} options.action - The action that failed (e.g., 'start', 'stop', 'join')
 * @param {boolean} options.showToast - Whether to show a toast notification (default: true)
 * @param {Function} options.onError - Optional callback for custom error handling
 * @param {boolean} options.logError - Whether to log the error (default: true)
 * @returns {Object} - Enhanced error information
 */
export const handleLivestreamError = (error, options = {}) => {
  const {
    action = 'operation',
    showToast = true,
    onError = null,
    logError = true
  } = options;

  // Extract error information from backend ApiError structure
  const apiError = error.response?.data;
  const statusCode = error.response?.status;
  const errorType = apiError?.errorType || 'GENERAL_ERROR';
  const errorMessage = apiError?.message || error.message || `Failed to ${action} livestream`;
  const errorId = apiError?.errorId || 'unknown';
  const timestamp = apiError?.timestamp || new Date().toISOString();
  const fieldErrors = apiError?.fieldErrors || null;

  // Get user-friendly message based on error type
  const userMessage = ERROR_TYPE_MESSAGES[errorType] || errorMessage;
  
  // Get HTTP status message if available
  const statusMessage = HTTP_STATUS_MESSAGES[statusCode];

  // Create enhanced error object
  const enhancedError = {

    isNetworkError: !error.response,
    isServerError: statusCode >= 500,
    isClientError: statusCode >= 400 && statusCode < 500,
    isAuthError: statusCode === 401 || statusCode === 403,
    isNotFoundError: statusCode === 404,
    isConflictError: statusCode === 409,
    isPaymentRequired: statusCode === 424 || errorType === 'LIVESTREAM_PAYMENT_REQUIRED'
  };

  // Log detailed error information
  if (logError) {
    // Error logging removed for production
  }

  // Show appropriate toast based on error type
  if (showToast) {
    if (enhancedError.isAuthError) {
      toast.error('Please log in to access this resource');
    } else if (enhancedError.isPaymentRequired) {
      toast.error('Payment required to access this livestream');
    } else if (enhancedError.isNetworkError) {
      toast.error('Network error. Please check your connection');
    } else if (enhancedError.isServerError) {
      toast.error('Server error. Please try again later');
    } else {
      toast.error(userMessage);
    }
  }

  // Call custom error handler if provided
  if (onError) {
    onError(enhancedError);
  }

  return enhancedError;
};

/**
 * Legacy error messages for backward compatibility
 */
export const LIVESTREAM_ERRORS = {
  FETCH_FAILED: 'Failed to fetch livestream details',
  START_FAILED: 'Failed to start livestream',
  STOP_FAILED: 'Failed to stop livestream',
  JOIN_FAILED: 'Failed to join livestream',
  DELETE_FAILED: 'Failed to delete livestream',
  UPDATE_FAILED: 'Failed to update livestream',
  RECORDING_CHECK_FAILED: 'Error checking for recording',
  DOWNLOAD_FAILED: 'Failed to download recording',
  NO_WATCH_URL: 'No watch URL provided',
  NO_LIVESTREAM_ID: 'No livestream ID provided',
  INVALID_STATUS: 'Invalid livestream status',
  PERMISSION_DENIED: 'You do not have permission to perform this action',
  NETWORK_ERROR: 'Network error occurred. Please check your connection',
  SERVER_ERROR: 'Server error occurred. Please try again later',
};

/**
 * Status-specific error messages
 */
export const STATUS_ERRORS = {
  IN_PROGRESS: {
    ALREADY_STARTED: 'Livestream is already in progress',
    CANNOT_JOIN: 'Cannot join livestream at this time',
  },
  STOPPED: {
    ALREADY_STOPPED: 'Livestream is already stopped',
    CANNOT_STOP: 'Cannot stop livestream at this time',
  },
  SCHEDULED: {
    NOT_STARTED: 'Livestream has not started yet',
    CANNOT_JOIN: 'Cannot join livestream before it starts',
  },
  COMPLETED: {
    ALREADY_ENDED: 'Livestream has already ended',
    CANNOT_JOIN: 'Cannot join completed livestream',
  },
};

/**
 * Utility function to handle field-specific validation errors
 * @param {Object} fieldErrors - Field errors from backend
 * @returns {Array} - Array of formatted field error messages
 */
export const formatFieldErrors = (fieldErrors) => {
  if (!fieldErrors || typeof fieldErrors !== 'object') {
    return [];
  }

  return Object.entries(fieldErrors).map(([field, message]) => ({

    formattedMessage: `${field}: ${message}`
  }));
};

/**
 * Utility function to check if error is a specific type
 * @param {Object} error - Enhanced error object
 * @param {string} errorType - Error type to check
 * @returns {boolean} - Whether error matches the type
 */
export const isErrorType = (error, errorType) => {
  return error.errorType === errorType;
};

/**
 * Utility function to get appropriate action based on error
 * @param {Object} error - Enhanced error object
 * @returns {string} - Suggested action for user
 */
export const getErrorAction = (error) => {
  if (error.isAuthError) {
    return 'Please log in and try again';
  } else if (error.isPaymentRequired) {
    return 'Please complete payment to continue';
  } else if (error.isNetworkError) {
    return 'Please check your internet connection';
  } else if (error.isServerError) {
    return 'Please try again in a few minutes';
  } else if (error.isNotFoundError) {
    return 'The requested resource was not found';
  } else if (error.isConflictError) {
    return 'Please refresh and try again';
  } else {
    return 'Please try again';
  }
};

/**
 * Utility function to determine if error is retryable
 * @param {Object} error - Enhanced error object
 * @returns {boolean} - Whether the error is retryable
 */
export const isRetryableError = (error) => {
  // Don't retry auth errors, not found, or client errors
  if (error.isAuthError || error.isNotFoundError || error.isClientError) {
    return false;
  }
  
  // Retry network errors and server errors
  return error.isNetworkError || error.isServerError;
}; 